import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../../../context/AuthContext';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

// 할인 코드 목록 (코드 → 할인율)
const DISCOUNT_CODES: Record<string, { rate: number; label: string }> = {
  'WELCOME10': { rate: 10, label: '신규 회원 10% 할인' },
  'MEMBER20': { rate: 20, label: '회원 특별 20% 할인' },
  'HEALTH15': { rate: 15, label: '건강식품 15% 할인' },
  'VIP30': { rate: 30, label: 'VIP 회원 30% 할인' },
};

export default function DiscountSection() {
  const { ref, isVisible } = useScrollAnimation();
  const { member, isLoggedIn, refreshMember } = useAuth();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [discountInfo, setDiscountInfo] = useState<{ rate: number; label: string } | null>(null);

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      setStatus('error');
      setMsg('로그인 후 할인 코드를 사용할 수 있습니다.');
      return;
    }
    if (!code.trim()) {
      setStatus('error');
      setMsg('할인 코드를 입력해 주세요.');
      return;
    }

    setStatus('loading');
    setMsg('');

    const upperCode = code.trim().toUpperCase();
    const found = DISCOUNT_CODES[upperCode];

    if (!found) {
      setStatus('error');
      setMsg('유효하지 않은 할인 코드입니다.');
      return;
    }

    if (member?.discount_used) {
      setStatus('error');
      setMsg('이미 할인 코드를 사용하셨습니다. 코드는 1회만 사용 가능합니다.');
      return;
    }

    try {
      // members 테이블에 할인 코드 저장
      const { error } = await supabase
        .from('members')
        .update({
          discount_code: upperCode,
          discount_used: true,
          updated_at: new Date().toISOString(),
        })
        .eq('member_id', member!.member_id);

      if (error) {
        setStatus('error');
        setMsg('할인 코드 적용 중 오류가 발생했습니다.');
        return;
      }

      await refreshMember();
      setDiscountInfo(found);
      setStatus('success');
      setMsg(`${found.label} 코드가 성공적으로 적용되었습니다!`);
      setCode('');
    } catch {
      setStatus('error');
      setMsg('오류가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  return (
    <section id="discount" className="py-16 md:py-20 px-5 md:px-8 bg-white">
      <div
        ref={ref}
        className={`max-w-2xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* 헤더 */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4 tracking-wide">
            DISCOUNT CODE
          </span>
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-900 mb-3"
            style={{ fontFamily: 'Noto Serif KR, serif' }}
          >
            회원 할인 코드
          </h2>
          <p className="text-sm md:text-base text-gray-500">
            보유하신 할인 코드를 입력하면 <strong className="text-amber-600">즉시 할인 혜택</strong>이 적용됩니다
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 md:p-10 border border-amber-100">
          {/* 로그인 안내 */}
          {!isLoggedIn && (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-100 rounded-xl mb-6">
              <span className="w-5 h-5 flex items-center justify-center text-amber-600 flex-shrink-0">
                <i className="ri-information-line text-base"></i>
              </span>
              <p className="text-sm text-amber-700">
                할인 코드 사용은 <strong>로그인 후</strong> 이용 가능합니다
              </p>
            </div>
          )}

          {/* 이미 사용한 경우 */}
          {isLoggedIn && member?.discount_used && (
            <div className="flex items-center gap-3 px-4 py-4 bg-emerald-50 rounded-xl mb-6 border border-emerald-200">
              <span className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                <i className="ri-check-line text-emerald-600 text-base w-4 h-4 flex items-center justify-center"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-emerald-700">할인 코드 적용 완료</p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  코드 <strong>{member.discount_code}</strong>가 적용되어 있습니다
                </p>
              </div>
            </div>
          )}

          {/* 성공 메시지 */}
          {status === 'success' && discountInfo && (
            <div className="text-center py-6 mb-4">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                <i className="ri-coupon-3-line text-amber-600 text-2xl w-7 h-7 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{discountInfo.label}</h3>
              <p className="text-sm text-gray-500 mb-2">{msg}</p>
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-full text-sm font-bold">
                <i className="ri-percent-line w-4 h-4 flex items-center justify-center"></i>
                {discountInfo.rate}% 할인 적용됨
              </div>
            </div>
          )}

          {/* 코드 입력 폼 */}
          {!(isLoggedIn && member?.discount_used) && status !== 'success' && (
            <form onSubmit={handleApply} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  할인 코드 입력
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-coupon-line text-base"></i>
                    </span>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="할인 코드를 입력하세요"
                      disabled={!isLoggedIn || status === 'loading'}
                      className="w-full pl-10 pr-4 py-3 border border-amber-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!isLoggedIn || status === 'loading'}
                    className="px-6 py-3 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {status === 'loading' ? (
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                    ) : (
                      '적용'
                    )}
                  </button>
                </div>
              </div>

              {/* 에러 메시지 */}
              {status === 'error' && msg && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                    <i className="ri-error-warning-line text-sm"></i>
                  </span>
                  <p className="text-xs text-red-600">{msg}</p>
                </div>
              )}
            </form>
          )}

          {/* 안내 사항 */}
          <div className="mt-6 pt-5 border-t border-amber-200">
            <p className="text-xs font-semibold text-gray-600 mb-2">할인 코드 안내</p>
            <ul className="flex flex-col gap-1.5">
              {[
                '할인 코드는 1인 1회만 사용 가능합니다',
                '할인 코드는 구매 시 자동으로 적용됩니다',
                '일부 상품은 할인 코드 적용이 제한될 수 있습니다',
                '할인 코드 관련 문의는 고객센터로 연락해 주세요',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-500">
                  <span className="w-3 h-3 flex items-center justify-center text-amber-400 flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-line text-xs"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
